'use client'

import { SoloViewerTokenFragment, SetBucketTokenFragment } from '@/gql/generated/graphql'
import { DetailText, BodyText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { getVerticalTitle } from '@utils/project'
import { AllPurposeLink } from '@components/all-purpose-link'
import { motion } from 'motion/react'
import { useState } from 'react'
import { gridItemTransition } from '@components/grid'
import { tokenShowRoute } from '@utils/routes'
import { createSerializer, parseAsBoolean } from 'nuqs'
import { getInvocationFromTokenId } from '@utils/common'

const tokenParamsSerializer = createSerializer({
  hideNavigationArrows: parseAsBoolean.withDefault(false)
})

// Union type for both token types
type TokenType = SoloViewerTokenFragment | SetBucketTokenFragment

type TokenGridItemProps = {
  token: TokenType
  hideDetails?: boolean
  hideNavigationArrows?: boolean
  extraComponent?: React.ReactNode
}

export const TokenGridItem = ({
  token,
  hideNavigationArrows = false,
  hideDetails = false,
  extraComponent
}: TokenGridItemProps): React.ReactElement => {
  const [opacity, setOpacity] = useState(0)

  // Handle different token types for rendering check
  const isRendered = token.image_id

  const tokenUrl = tokenParamsSerializer(tokenShowRoute(token.contract_address, token.token_id), {
    hideNavigationArrows
  })

  const tokenId = getInvocationFromTokenId(token.token_id)

  return (
    <motion.div
      className="group relative flex w-full flex-col items-center"
      key={token.id}
      id={token.id}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={gridItemTransition}
    >
      <AllPurposeLink href={tokenUrl} className="relative flex w-full flex-col items-center justify-start text-center">
        {/* Image Section */}
        {isRendered ? (
          <motion.div
            className={`bg-surface-low relative flex aspect-square h-auto w-full rounded-[2px]`}
            layout
            transition={{ duration: 0.3 }}
            animate={{ opacity: opacity }}
          >
            <motion.div
              className="relative h-full w-full"
              layout
              transition={{ duration: 0.3 }}
              animate={{ opacity: opacity }}
            >
              <ImageWithCustomLoader
                onLoad={() => setOpacity(1)}
                className="p-md drop-shadow-[3px_3px_6px_rgba(0,0,0,0.20)] transition-all duration-300 hover:scale-[1.01] hover:drop-shadow-[3px_5px_7px_rgba(0,0,0,0.20)]"
                src={token.media_url ?? ''}
                blurHash={token.image?.metadata?.blurhash ?? undefined}
                fill
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center'
                }}
                alt={'token image'}
                sizes="33vw"
              />
            </motion.div>
          </motion.div>
        ) : (
          <div className="bg-surface-container gap-y-xs flex h-full min-h-[150px] w-full flex-col items-center justify-center">
            <DetailText className="font-medium">{`Rendering #${tokenId}`}</DetailText>
          </div>
        )}

        {/* Text Section */}
        {hideDetails ? null : (
          <motion.div
            layout="position"
            transition={{ duration: 0.3 }}
            className="mt-md flex flex-col items-center justify-center"
          >
            <div className="gap-xs flex flex-col items-center justify-center">
              <DetailText className="text-on-surface-variant font-medium uppercase">
                {getVerticalTitle(token.project)}
              </DetailText>
              {extraComponent}
              <BodyText className="font-medium">
                {token.project?.name} #{tokenId}
              </BodyText>
              <BodyText>{token.project?.artist_name}</BodyText>
            </div>
          </motion.div>
        )}
      </AllPurposeLink>
    </motion.div>
  )
}
